/**
 * Client Feedback Module
 * Capture and track all client feedback with integration to Error Tracking and Change Orders
 * MD3 Design
 */
import { useState, useEffect } from "react";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { api, toast } from "../App";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  AlertCircle,
  FileText,
  Mail,
  Phone,
  Users,
  ChevronRight,
  ExternalLink,
  ChevronsUpDown,
  Check,
} from "lucide-react";
import { cn } from "@/lib/utils";

const MODES = ["Email", "Markup Drawings", "Phone", "Meeting"];
const FEEDBACK_TYPES = ["Error", "Clarification", "Change", "Additional Information", "Void"];
const REVIEW_STATUSES = ["Pending", "Under Review", "Reviewed"];

export default function ClientFeedback({ user }) {
  const [feedbackList, setFeedbackList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [errorMasters, setErrorMasters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterType, setFilterType] = useState("all");

  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showReviewDialog, setShowReviewDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [showChangeOrderDialog, setShowChangeOrderDialog] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [openProjectCombobox, setOpenProjectCombobox] = useState(false);

  // Form states
  const [formData, setFormData] = useState({
    project_id: "",
    date_received: new Date().toISOString().split("T")[0],
    mode: "",
    description: "",
  });

  const [reviewData, setReviewData] = useState({
    review_status: "",
    feedback_type: "",
    assigned_to: "",
  });

  const [errorFormData, setErrorFormData] = useState({
    error_master_id: "",
    work_type: "Client Feedback",
    assigned_to: "",
    quantity: 1,
    remarks: "",
  });

  const [changeOrderData, setChangeOrderData] = useState({
    change_order_name: "",
    description: "",
    estimated_value: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [feedbackRes, projectsRes, employeesRes, errorMastersRes] = await Promise.all([
        api.get("/client-feedback"),
        api.get("/projects"),
        api.get("/employees"),
        api.get("/error-master"),
      ]);
      setFeedbackList(feedbackRes.data);
      setProjects(projectsRes.data);
      setEmployees(employeesRes.data);
      // Filter error masters to only show those with "Client Feedback" work type
      const clientFeedbackErrors = errorMastersRes.data.filter(em => 
        em.work_types && em.work_types.includes("Client Feedback")
      );
      setErrorMasters(clientFeedbackErrors);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.project_id || !formData.date_received || !formData.mode || !formData.description) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      await api.post("/client-feedback", formData);
      toast.success("Feedback created successfully");
      setShowAddDialog(false);
      setFormData({
        project_id: "",
        date_received: new Date().toISOString().split("T")[0],
        mode: "",
        description: "",
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create feedback");
    }
  };

  const handleUpdate = async () => {
    if (!selectedFeedback) return;

    try {
      await api.put(`/client-feedback/${selectedFeedback.id}`, formData);
      toast.success("Feedback updated successfully");
      setShowEditDialog(false);
      setSelectedFeedback(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update feedback");
    }
  };

  const handleReview = async () => {
    if (!selectedFeedback || !reviewData.review_status) return;

    if (reviewData.review_status === "Reviewed" && !reviewData.feedback_type) {
      toast.error("Please select feedback type");
      return;
    }

    try {
      await api.put(`/client-feedback/${selectedFeedback.id}`, reviewData);
      toast.success("Feedback reviewed successfully");
      setShowReviewDialog(false);
      setSelectedFeedback(null);
      setReviewData({ review_status: "", feedback_type: "", assigned_to: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to review feedback");
    }
  };

  const handleCreateError = async () => {
    if (!selectedFeedback || !errorFormData.error_master_id || !errorFormData.assigned_to) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      await api.post(`/client-feedback/${selectedFeedback.id}/create-error`, errorFormData);
      toast.success("Error entry created successfully");
      setShowErrorDialog(false);
      setSelectedFeedback(null);
      setErrorFormData({ error_master_id: "", work_type: "Client Feedback", assigned_to: "", remarks: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create error");
    }
  };

  const handleCreateChangeOrder = async () => {
    if (!selectedFeedback || !changeOrderData.change_order_name) {
      toast.error("Please enter change order name");
      return;
    }

    try {
      await api.post(`/client-feedback/${selectedFeedback.id}/create-change-order`, changeOrderData);
      toast.success("Change order created successfully");
      setShowChangeOrderDialog(false);
      setSelectedFeedback(null);
      setChangeOrderData({ change_order_name: "", description: "", estimated_value: 0 });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create change order");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this feedback?")) return;

    try {
      await api.delete(`/client-feedback/${id}`);
      toast.success("Feedback deleted successfully");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete feedback");
    }
  };

  const openEditDialog = (feedback) => {
    setSelectedFeedback(feedback);
    setFormData({
      project_id: feedback.project_id,
      date_received: feedback.date_received,
      mode: feedback.mode,
      description: feedback.description,
    });
    setShowEditDialog(true);
  };

  const openReviewDialog = (feedback) => {
    setSelectedFeedback(feedback);
    setReviewData({
      review_status: feedback.review_status,
      feedback_type: feedback.feedback_type || "",
      assigned_to: feedback.assigned_to || "",
    });
    setShowReviewDialog(true);
  };

  const openErrorDialog = (feedback) => {
    setSelectedFeedback(feedback);
    setErrorFormData({
      error_master_id: "",
      work_type: "Client Feedback",
      assigned_to: feedback.assigned_to || "",
      remarks: "",
    });
    setShowErrorDialog(true);
  };

  const openChangeOrderDialog = (feedback) => {
    setSelectedFeedback(feedback);
    setChangeOrderData({
      change_order_name: `Change from ${feedback.feedback_number}`,
      description: feedback.description,
      estimated_value: 0,
    });
    setShowChangeOrderDialog(true);
  };

  const getStatusBadge = (status) => {
    const colors = {
      Pending: "bg-amber-100 text-amber-800",
      "Under Review": "bg-blue-100 text-blue-800",
      Reviewed: "bg-green-100 text-green-800",
    };
    return <Badge className={colors[status] || "bg-gray-100"}>{status}</Badge>;
  };

  const getTypeBadge = (type) => {
    if (!type) return <Badge className="bg-gray-100 text-gray-600">-</Badge>;
    const colors = {
      Error: "bg-red-100 text-red-800",
      Clarification: "bg-purple-100 text-purple-800",
      Change: "bg-orange-100 text-orange-800",
      "Additional Information": "bg-cyan-100 text-cyan-800",
      Void: "bg-gray-100 text-gray-800",
    };
    return <Badge className={colors[type] || "bg-gray-100"}>{type}</Badge>;
  };

  const getModeIcon = (mode) => {
    switch (mode) {
      case "Email":
        return <Mail className="h-4 w-4" />;
      case "Phone":
        return <Phone className="h-4 w-4" />;
      case "Meeting":
        return <Users className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  const filteredFeedback = feedbackList.filter((fb) => {
    const matchesSearch =
      fb.feedback_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fb.project_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fb.client_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fb.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === "all" || fb.review_status === filterStatus;
    const matchesType = filterType === "all" || fb.feedback_type === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  const canManage = user?.access_level === "Manager" || user?.access_level === "Admin";

  return (
    <Layout user={user}>
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-md3-on-surface">Client Feedback</h1>
          <p className="text-sm text-md3-on-surface-variant">Track and manage client feedback</p>
        </div>
        {canManage && (
          <Button
            onClick={() => setShowAddDialog(true)}
            className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Feedback
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="bg-md3-surface border-md3-outline-variant">
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-md3-on-surface-variant" />
                <Input
                  placeholder="Search feedback..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {REVIEW_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {FEEDBACK_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Feedback Table */}
      <Card className="bg-md3-surface border-md3-outline-variant">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-md3-surface-container">
                <TableHead className="font-medium">Feedback #</TableHead>
                <TableHead className="font-medium">Date</TableHead>
                <TableHead className="font-medium">Client / Project</TableHead>
                <TableHead className="font-medium">Mode</TableHead>
                <TableHead className="font-medium">Type</TableHead>
                <TableHead className="font-medium">Status</TableHead>
                <TableHead className="font-medium">Linked</TableHead>
                <TableHead className="font-medium text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : filteredFeedback.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-md3-on-surface-variant">
                    No feedback found
                  </TableCell>
                </TableRow>
              ) : (
                filteredFeedback.map((fb) => (
                  <TableRow key={fb.id} className="hover:bg-md3-surface-container-high/50">
                    <TableCell className="font-medium">{fb.feedback_number}</TableCell>
                    <TableCell>{fb.date_received}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="font-medium">{fb.client_name}</div>
                        <div className="text-md3-on-surface-variant">
                          {fb.project_number} - {fb.project_name}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getModeIcon(fb.mode)}
                        <span>{fb.mode}</span>
                      </div>
                    </TableCell>
                    <TableCell>{getTypeBadge(fb.feedback_type)}</TableCell>
                    <TableCell>{getStatusBadge(fb.review_status)}</TableCell>
                    <TableCell>
                      {fb.linked_error_id && (
                        <Badge 
                          className="bg-red-50 text-red-700 mr-1 cursor-pointer hover:bg-red-100" 
                          title={`Error ID: ${fb.linked_error_id}`}
                        >
                          Error
                        </Badge>
                      )}
                      {fb.linked_change_order_id && (
                        <Badge 
                          className="bg-orange-50 text-orange-700 cursor-pointer hover:bg-orange-100"
                          title={`Change Order: ${fb.linked_change_order_name || fb.linked_change_order_id}`}
                        >
                          CO: {fb.linked_change_order_name || "View"}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {canManage && fb.review_status !== "Reviewed" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openReviewDialog(fb)}
                            title="Review"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        )}
                        {canManage && fb.feedback_type === "Error" && !fb.linked_error_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openErrorDialog(fb)}
                            className="text-red-600"
                            title="Create Error"
                          >
                            <AlertCircle className="h-4 w-4" />
                          </Button>
                        )}
                        {canManage && fb.feedback_type === "Change" && !fb.linked_change_order_id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openChangeOrderDialog(fb)}
                            className="text-orange-600"
                            title="Create Change Order"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        )}
                        {canManage && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(fb)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        {user?.access_level === "Admin" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(fb.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog || showEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowAddDialog(false);
          setShowEditDialog(false);
          setSelectedFeedback(null);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{showEditDialog ? "Edit Feedback" : "Add New Feedback"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Project *</Label>
              {showEditDialog ? (
                <div className="p-2 bg-gray-100 rounded-md text-sm text-gray-700">
                  {(() => {
                    const project = projects.find(p => p.id === formData.project_id);
                    return project 
                      ? `${project.project_number || "N/A"} - ${project.name || project.project_name || "Unnamed"}`
                      : "Project not found";
                  })()}
                </div>
              ) : (
                <Popover open={openProjectCombobox} onOpenChange={setOpenProjectCombobox}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={openProjectCombobox}
                      className="w-full justify-between overflow-hidden"
                    >
                      <span className="truncate">
                        {formData.project_id
                          ? (() => {
                              const project = projects.find(p => p.id === formData.project_id);
                              return project 
                                ? `${project.project_number || "N/A"} - ${project.name || project.project_name || "Unnamed"}`
                                : "Select project...";
                            })()
                          : "Select project..."}
                      </span>
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search project..." />
                      <CommandList>
                        <CommandEmpty>No project found.</CommandEmpty>
                        <CommandGroup>
                          {projects.map((p) => (
                            <CommandItem
                              key={p.id}
                              value={`${p.project_number || ""} ${p.name || p.project_name || ""}`}
                              onSelect={() => {
                                setFormData({ ...formData, project_id: p.id });
                                setOpenProjectCombobox(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.project_id === p.id ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <span className="truncate">
                                {p.project_number || "N/A"} - {p.name || p.project_name || "Unnamed"}
                              </span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            </div>
            <div className="space-y-2">
              <Label>Date Received *</Label>
              <Input
                type="date"
                value={formData.date_received}
                onChange={(e) => setFormData({ ...formData, date_received: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Mode *</Label>
              <Select
                value={formData.mode}
                onValueChange={(v) => setFormData({ ...formData, mode: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  {MODES.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                placeholder="Describe the feedback..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddDialog(false);
              setShowEditDialog(false);
            }}>
              Cancel
            </Button>
            <Button onClick={showEditDialog ? handleUpdate : handleCreate}>
              {showEditDialog ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={showReviewDialog} onOpenChange={setShowReviewDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Review Feedback</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-md3-surface-container rounded-lg">
              <p className="font-medium">{selectedFeedback?.feedback_number}</p>
              <p className="text-sm text-md3-on-surface-variant">{selectedFeedback?.description}</p>
            </div>
            <div className="space-y-2">
              <Label>Review Status *</Label>
              <Select
                value={reviewData.review_status}
                onValueChange={(v) => setReviewData({ ...reviewData, review_status: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {REVIEW_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {reviewData.review_status === "Reviewed" && (
              <div className="space-y-2">
                <Label>Feedback Type *</Label>
                <Select
                  value={reviewData.feedback_type}
                  onValueChange={(v) => setReviewData({ ...reviewData, feedback_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {FEEDBACK_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReviewDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleReview}>Save Review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Error Dialog */}
      <Dialog open={showErrorDialog} onOpenChange={setShowErrorDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Error Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm font-medium text-red-800">
                Creating error from: {selectedFeedback?.feedback_number}
              </p>
              <p className="text-sm text-red-700">{selectedFeedback?.description}</p>
            </div>
            <div className="space-y-2">
              <Label>Error Type *</Label>
              <Select
                value={errorFormData.error_master_id}
                onValueChange={(v) => setErrorFormData({ ...errorFormData, error_master_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select error type" />
                </SelectTrigger>
                <SelectContent>
                  {errorMasters.map((em) => (
                    <SelectItem key={em.id} value={em.id}>
                      {em.description || em.name || em.error_type_name} ({em.weightage_points} pts)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Assign To *</Label>
              <Select
                value={errorFormData.assigned_to}
                onValueChange={(v) => setErrorFormData({ ...errorFormData, assigned_to: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map((emp) => (
                    <SelectItem key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} - {emp.department}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Quantity *</Label>
              <Input
                type="number"
                min="1"
                value={errorFormData.quantity}
                onChange={(e) => setErrorFormData({ ...errorFormData, quantity: parseFloat(e.target.value) || 1 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Textarea
                value={errorFormData.remarks}
                onChange={(e) => setErrorFormData({ ...errorFormData, remarks: e.target.value })}
                rows={3}
                placeholder="Additional remarks..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowErrorDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateError} className="bg-red-600 hover:bg-red-700">
              Create Error
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Change Order Dialog */}
      <Dialog open={showChangeOrderDialog} onOpenChange={setShowChangeOrderDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Change Order</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
              <p className="text-sm font-medium text-orange-800">
                Creating change order from: {selectedFeedback?.feedback_number}
              </p>
              <p className="text-sm text-orange-700">
                Project: {selectedFeedback?.project_number} - {selectedFeedback?.project_name}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Change Order Name *</Label>
              <Input
                value={changeOrderData.change_order_name}
                onChange={(e) => setChangeOrderData({ ...changeOrderData, change_order_name: e.target.value })}
                placeholder="Enter name"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={changeOrderData.description}
                onChange={(e) => setChangeOrderData({ ...changeOrderData, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="space-y-2">
              <Label>Estimated Value</Label>
              <Input
                type="number"
                value={changeOrderData.estimated_value}
                onChange={(e) => setChangeOrderData({ ...changeOrderData, estimated_value: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangeOrderDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateChangeOrder} className="bg-orange-600 hover:bg-orange-700">
              Create Change Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
